import React from "react";
import { useAppStore } from "@/hooks/useAppStore";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/lib/translate";
import { Button } from "@/components/ui/button";
import { Speech, PanelLeft, PanelLeftClose } from "lucide-react";
import { FONT_SIZES, SIZE_CLASSES } from "@/components/chat/PresentationChatLayout";

export const AdminSettingsPanel: React.FC = () => {
    const preferredLanguage = useAppStore((state) => state.preferredLanguage);
    const presentationSettings = useAppStore((state) => state.presentationSettings);
    const setPresentationSettings = useAppStore((state) => state.setPresentationSettings);
    const t = useTranslation(preferredLanguage);

    return (
        <div className="flex flex-col h-full bg-background/30 border-l border-neutral-500/30 p-4 w-64">
            <h3 className="text-sm font-semibold mb-4">{t("Presentation Settings")}</h3>

            <div className="space-y-4">
                {/* VAD Toggle */}
                <div className="space-y-2">
                    <label className="text-xs text-foreground">{t("Voice Detection")}</label>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPresentationSettings({ vadEnabled: !presentationSettings.vadEnabled })}
                        className={cn(
                            "w-full justify-start mt-2",
                            presentationSettings.vadEnabled ? "text-green-500 border-green-500" : ""
                        )}
                    >
                        <Speech size={16} className="mr-2" />
                        {presentationSettings.vadEnabled ? t("VAD Enabled") : t("VAD Disabled")}
                    </Button>
                </div>

                {/* Font Size */}
                <div className="space-y-2">
                    <label className="text-xs text-foreground">{t("Font Size")}</label>
                    <div className="flex gap-2 mt-2">
                        {FONT_SIZES.map((size) => (
                            <button
                                key={size}
                                onClick={() => setPresentationSettings({ fontSize: size })}
                                className={cn(
                                    "flex-1 rounded px-2 py-1 transition-colors border",
                                    presentationSettings.fontSize === size
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "hover:bg-muted border-border"
                                )}
                                title={t(`Font size: {{size}}`, { size })}
                            >
                                <span className={`${SIZE_CLASSES[size]} leading-none`}>T</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Sidepanel Toggle */}
                <div className="space-y-2">
                    <label className="text-xs text-foreground">{t("Q&A Sidepanel")}</label>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPresentationSettings({ sidePanelOpen: !presentationSettings.sidePanelOpen })}
                        className="w-full justify-start mt-2"
                    >
                        {presentationSettings.sidePanelOpen ? (
                            <>
                                <PanelLeftClose size={16} className="mr-2" />
                                {t("Hide Sidepanel")}
                            </>
                        ) : (
                            <>
                                <PanelLeft size={16} className="mr-2" />
                                {t("Show Sidepanel")}
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};
